import React, { useState, useEffect, useCallback, useContext } from 'react'
import { Box, Typography, Button, Skeleton } from '@mui/material'
import { CurrencyContext } from '../currencyContext'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import VisibilityIcon from '@mui/icons-material/Visibility'
import fallbackImg from '../resources/logo.jpeg'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000'
const BANNER_HEIGHT_DESKTOP = 440
const BANNER_HEIGHT_MOBILE  = 220

const buildImageUrl = (path) => {
    if (!path) return null
    if (path.startsWith('http')) return path.replace('https//', 'https://')
    return `${BASE_URL}${path}`
}

/**
 * Hero banner slideshow for the home page.
 * Features:
 *  - Skeleton placeholder while first image loads
 *  - Persistent bottom gradient overlay (always readable, no hover needed)
 *  - "View Property" CTA button centred on hover
 *  - Auto-advances every 5 s, dot indicators, chevron navigation
 */


function OverlayImage(props) {
    const items    = props.items || []
    const currency = useContext(CurrencyContext).value

    const [current,    setCurrent]   = useState(0)
    const [width,      setWidth]     = useState(window.innerWidth)
    const [hover,      setHover]     = useState(false)
    const [imgLoaded,  setImgLoaded] = useState(false)

    const isMobile = width < 720
    const height   = isMobile ? BANNER_HEIGHT_MOBILE : BANNER_HEIGHT_DESKTOP

    const item     = items[current] || null
    const slug     = item?.slug
    const title    = item?.title || ''
    const location = item ? [item.location, item.parish_display].filter(Boolean).join(', ') : ''
    const rawPrice = item?.display_price ? parseFloat(item.display_price) : 0
    const price    = rawPrice * currency.value
    const isSold   = item?.status === 'sold'
    const imgSrc = item?.main_image ? buildImageUrl(item.main_image) : fallbackImg

    // Reset loaded flag when slide changes
    useEffect(() => { setImgLoaded(false) }, [current])

    useEffect(() => {
        const handler = () => setWidth(window.innerWidth)
        window.addEventListener('resize', handler)
        return () => window.removeEventListener('resize', handler)
    }, [])

    const increment = useCallback(() => {
        setCurrent(c => (c >= items.length - 1 ? 0 : c + 1))
    }, [items.length])

    const decrement = useCallback(() => {
        setCurrent(c => (c <= 0 ? items.length - 1 : c - 1))
    }, [items.length])

    useEffect(() => {
        if (items.length <= 1) return
        const loop = setInterval(increment, 5000)
        return () => clearInterval(loop)
    }, [increment, items.length])

    function handleClick() {
        if (props.onClick && slug) props.onClick(slug)
    }

    if (items.length === 0) {
        return (
            <Skeleton
                variant='rectangular'
                width='100%'
                height={height}
                animation='wave'
                sx={{ bgcolor: '#E0E7EF' }}
            />
        )
    }

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                height,
                overflow: 'hidden',
                bgcolor: '#1a1a2e',
                userSelect: 'none',
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            {/* ── Skeleton shown until image is loaded ── */}
            {!imgLoaded && (
                <Skeleton
                    variant='rectangular'
                    width='100%'
                    height={height}
                    animation='wave'
                    sx={{ position: 'absolute', top: 0, left: 0, bgcolor: '#E0E7EF', zIndex: 1 }}
                />
            )}

            {/* ── Slide image ── */}
            <img
                key={current}
                src={imgSrc}
                alt={title}
                onLoad={() => setImgLoaded(true)}
                style={{
                    position: 'absolute',
                    top: 0, left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: imgLoaded ? 1 : 0,
                    transition: 'opacity 0.5s ease',
                    zIndex: 0,
                }}
            />

            {/* ── Persistent bottom gradient overlay ── */}
            <Box sx={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                height: '55%',
                background: 'linear-gradient(to top, rgba(10,20,40,0.82) 0%, rgba(10,20,40,0.3) 60%, transparent 100%)',
                zIndex: 2,
                pointerEvents: 'none',
            }} />

            {/* ── Text info (always visible at bottom) ── */}
            <Box sx={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                zIndex: 3,
                px: { xs: 2, md: 4 },
                pb: { xs: 2, md: 3 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.25,
            }}>
                {location && (
                    <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                        {location}
                    </Typography>
                )}
                {title && (
                    <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
                        {title}
                    </Typography>
                )}
                <Typography
                    variant={isMobile ? 'body1' : 'h6'}
                    sx={{
                        color: isSold ? 'rgba(255,255,255,0.6)' : '#6fcf97',
                        fontWeight: 700,
                        textDecoration: isSold ? 'line-through' : 'none',
                    }}
                >
                    {price.toLocaleString('en-US', { style: 'currency', currency: currency.name })}
                    {isSold && <span style={{ marginLeft: 8, textDecoration: 'none', color: '#eb5757' }}>SOLD</span>}
                </Typography>

                {/* ── "View Property" CTA — slides up on hover ── */}
                <Box sx={{
                    mt: 0.5,
                    opacity: hover ? 1 : 0,
                    transform: hover ? 'translateY(0)' : 'translateY(6px)',
                    transition: 'opacity 0.25s ease, transform 0.25s ease',
                }}>
                    <Button
                        onClick={handleClick}
                        variant='contained'
                        size={isMobile ? 'small' : 'medium'}
                        startIcon={<VisibilityIcon />}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                    >
                        View Property
                    </Button>
                </Box>
            </Box>

            {/* ── Left chevron ── */}
            {items.length > 1 && (
                <Box
                    onClick={e => { e.stopPropagation(); decrement() }}
                    sx={{
                        position: 'absolute', left: 0, top: 0, bottom: 0,
                        zIndex: 4,
                        display: 'flex', alignItems: 'center', px: 1,
                        cursor: 'pointer',
                        opacity: hover ? 1 : 0,
                        transition: 'opacity 0.2s',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.25)' },
                    }}
                >
                    <ChevronLeftIcon sx={{ color: '#fff', fontSize: 36 }} />
                </Box>
            )}

            {/* ── Right chevron ── */}
            {items.length > 1 && (
                <Box
                    onClick={e => { e.stopPropagation(); increment() }}
                    sx={{
                        position: 'absolute', right: 0, top: 0, bottom: 0,
                        zIndex: 4,
                        display: 'flex', alignItems: 'center', px: 1,
                        cursor: 'pointer',
                        opacity: hover ? 1 : 0,
                        transition: 'opacity 0.2s',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.25)' },
                    }}
                >
                    <ChevronRightIcon sx={{ color: '#fff', fontSize: 36 }} />
                </Box>
            )}

            {/* ── Dot indicators ── */}
            {items.length > 1 && (
                <Box sx={{
                    position: 'absolute', bottom: 10, right: 14,
                    zIndex: 4,
                    display: 'flex', gap: 0.75,
                }}>
                    {items.map((_, i) => (
                        <Box
                            key={i}
                            onClick={e => { e.stopPropagation(); setCurrent(i) }}
                            sx={{
                                width: i === current ? 20 : 8,
                                height: 8,
                                borderRadius: 4,
                                bgcolor: i === current ? '#fff' : 'rgba(255,255,255,0.4)',
                                cursor: 'pointer',
                                transition: 'width 0.3s ease, background-color 0.3s ease',
                            }}
                        />
                    ))}
                </Box>
            )}
        </Box>
    )
}

export default React.memo(OverlayImage)
